/**
 * 68.39: the bot commander's airstrike rule — where its side's next one goes, on the side's knowledge only (its picture,
 * core/picture.ts: shared tracks and its instruments' sightings — never the truth, never what no one told it), after
 * 65.16's strike rule (ai/strikeRule.ts). In order:
 *   1. a gun crew: a known enemy within STRIKE_CREW_PEOPLE of an enemy emplacement;
 *   2. a cluster: the known enemy with the most known enemies inside the stick's reach (AIRSTRIKE_RULE.minCluster or more);
 *   3. a held point blocking its side: a point another side holds with AIRSTRIKE_RULE.minAtPoint or more known there.
 * Never with one of its own people within the strike's reach (friendliesInStrikeReach), nor at a belief fainter than
 * STRIKE_MIN_CONFIDENCE. Its pacing over the match clock is the side's spend dial (GameConfig.airstrikeSpend, decision
 * 188's first "when to spend"), pacing over the match clock (not a threshold of worth: every strike needs a target worth
 * one): `early` spends on the first target worth one; `even` (the default) no faster than the match's elapsed share of
 * the side's stock — the k-th of N no sooner than k/N of the match (the first at once); `late` keeps the last one until
 * the match's final third. Pure.
 */
import type { GameState, Vec3 } from '../types';
import { knownMounts, underKnownMount, shareThrough } from '../core/mountIntel';
import { AIRSTRIKE, EXPLOSIONS, PERSON_BODY_HEIGHT, PHYS_DT } from '../constants';
import { vecDot, vecNormalize } from '../math';
import { dacos } from '../core/dmath';
import { personSetOf } from '../core/scaleProfile';
import { teamPicture } from '../core/picture';
import { airstrikesLeft, friendliesInStrikeReach, strikeInGunReach } from '../core/airstrike';
import { structureTeam } from '../core/body';
import { knownStructure } from '../core/intelRecord';   // 69.73 step 2b
import { STRIKE_CREW_PEOPLE, STRIKE_MIN_CONFIDENCE } from './strikeRule';

/** The rule's dials: the smallest cluster worth one, and the fewest known at a held point. */
export const AIRSTRIKE_RULE = { minCluster: 3, minAtPoint: 2 };

/** May the side spend one now, by its pacing over the match clock? */
export function airstrikePaceAllows(state: GameState, team: number, tick: number): boolean {
  const a = state.airstrikes, left = airstrikesLeft(state, team);
  if (!a || left <= 0) return false;
  const spend = state.config.airstrikeSpend?.[team] ?? 'even';
  if (spend === 'early') return true;
  const limit = state.config.actionTimeLimit;
  const elapsed = limit && limit > 0 ? Math.min(1, (tick * PHYS_DT) / limit) : 1;   // (no clock: as early)
  if (spend === 'late') return left > 1 || elapsed >= 2 / 3;
  const issued = a.issued[team] ?? left, used = issued - left;
  return used < Math.ceil(issued * elapsed);
}

/** 69.9 step 5: a strike's target with what the known mounts make of it — `salvo` 2 when the commander doubles it to saturate a mount, `mount` the mount it strikes instead. */
export interface StrikeCall { planetIndex: number; surfaceDir: Vec3; salvo?: number; mount?: number }

/**
 * Where the side's bot commander calls its next airstrike now, or null. 69.9 step 5 (the owner's point defence; core/mountIntel.ts):
 * a target under a mount the side has seen is weighed by the share of the strike expected through (`shareThrough`) — a
 * target the mounts do not cover is taken first at equal rank; a covered one, with two or more strikes left, is answered by
 * a strike on the nearest covering mount's radar head first (when no friendly stands in that reach), else by two strikes
 * back to back (`salvo` 2: the mount's drum and interval are what a stick saturates); with one left, the covered target as
 * it is — a blunted strike is a strike. Only mounts the side has seen; none known, the rule as before.
 */
export function airstrikeTarget(state: GameState, team: number, tick: number): StrikeCall | null {
  if (!airstrikePaceAllows(state, team, tick)) return null;
  const picks = airstrikePicks(state, team, tick).filter(p => strikeInGunReach(state, team, p.planetIndex, p.surfaceDir));   // (69.13 (b): with the towed guns, within their reach)
  if (!picks.length) return null;
  const known = knownMounts(state, team);
  if (!known.length) return picks[0];
  const left = airstrikesLeft(state, team);
  const open = picks.find(p => shareThrough(state, team, p.planetIndex, p.surfaceDir) >= 1);
  if (open) return open;
  const t = picks[0];
  if (left >= 2) {
    const over = underKnownMount(state, team, t.planetIndex, t.surfaceDir, true);   // (69.50 step 2a: a mount that stops rounds, not a site)
    if (over && friendliesInStrikeReach(state, team, over.planetIndex, over.surfaceDir) === 0 && strikeInGunReach(state, team, over.planetIndex, over.surfaceDir)) return { planetIndex: over.planetIndex, surfaceDir: over.surfaceDir, mount: over.id };
    return { ...t, salvo: 2 };
  }
  return t;
}

/** The targets worth a strike now, in the rule's order (a gun crew, a cluster, a held point), each clear of the side's own people. */
function airstrikePicks(state: GameState, team: number, tick: number): Array<{ planetIndex: number; surfaceDir: Vec3 }> {
  const picks: Array<{ planetIndex: number; surfaceDir: Vec3 }> = [];
  const ps = personSetOf(state), person = PERSON_BODY_HEIGHT * ps.k;
  const reach = ((state.config.airstrikeRadius ?? AIRSTRIKE.radiusPeople) + EXPLOSIONS[AIRSTRIKE.explosion].blast!.radiusPeople) * person;   // (68.39b: its circle and a burst)
  const known = teamPicture(state, team, tick).filter(b => b.unitId !== undefined && b.confidence >= STRIKE_MIN_CONFIDENCE).sort((a, b) => a.unitId! - b.unitId!);
  const arc = (pi: number, a: Vec3, b: Vec3) => dacos(Math.max(-1, Math.min(1, vecDot(vecNormalize(a), vecNormalize(b))))) * state.planets[pi].radius;
  const safe = (pi: number, d: Vec3) => friendliesInStrikeReach(state, team, pi, d) === 0;
  // 1. a gun crew
  // (69.73 step 2b, with intelRecord: the guns the side knows — in its record, not seen down)
  const knownStanding = (id: number) => { const e = knownStructure(state, team, id); return !!e && e.state === undefined; };
  const guns = state.structures.filter(s => s.seats?.length && s.health > 0 && structureTeam(state, s) !== team && structureTeam(state, s) !== null && (!state.config.intelRecord || knownStanding(s.id))).sort((a, b) => a.id - b.id);
  for (const g of guns) {
    const crew = known.find(k => k.planetIndex === g.planetIndex && arc(g.planetIndex, k.surfaceDir, g.surfaceDir) <= STRIKE_CREW_PEOPLE * person);
    if (crew && safe(crew.planetIndex, crew.surfaceDir)) { picks.push({ planetIndex: crew.planetIndex, surfaceDir: crew.surfaceDir }); break; }
  }
  // 2. a cluster
  let best: { planetIndex: number; surfaceDir: Vec3; n: number } | null = null;
  for (const k of known) {
    const n = known.filter(o => o.planetIndex === k.planetIndex && arc(k.planetIndex, o.surfaceDir, k.surfaceDir) <= reach).length;
    if (n >= AIRSTRIKE_RULE.minCluster && (!best || n > best.n) && safe(k.planetIndex, k.surfaceDir)) best = { planetIndex: k.planetIndex, surfaceDir: k.surfaceDir, n };
  }
  if (best) picks.push({ planetIndex: best.planetIndex, surfaceDir: best.surfaceDir });
  // 3. a held point blocking it: another side's, with enough of them known there
  for (const site of (state.modeState.points?.sites ?? []).filter(s => s.owner !== null && s.owner !== undefined && s.owner !== team)) {
    const there = known.filter(k => k.planetIndex === site.planetIndex && arc(site.planetIndex, k.surfaceDir, site.surfaceDir) <= reach);
    if (there.length >= AIRSTRIKE_RULE.minAtPoint && safe(site.planetIndex, site.surfaceDir)) { picks.push({ planetIndex: site.planetIndex, surfaceDir: site.surfaceDir }); break; }
  }
  return picks;
}
